import { ReactNode } from 'react'
import { Link } from 'react-router'
import ArrowBack from '@mui/icons-material/ArrowBack'
import { Box, Button, Container, Stack } from '@mui/material'
import { visuallyHidden } from '@/utils/visually-hidden'
import SettingsHeading from './SettingsHeading'

type SettingsPageProps = {
  /** The page name. Match the label in the settings nav. */
  title: ReactNode
  /** One or two sentences on what this page controls. */
  description?: ReactNode
  /**
   * The page's primary action, shown on the right of the title. Use a text
   * button. Contained buttons are for the next step the user should take,
   * like Save.
   */
  actions?: ReactNode
  /**
   * A button back to the parent page, shown above the title. For a page you
   * open from a list, like a model's page in the Model Library.
   */
  back?: { label: string; to: string }
  /**
   * Hide the heading on screen but keep it for screen readers, for a page
   * whose content already says what it is. The description and actions are
   * hidden with it.
   *
   * Only for explicit, exceptional cases, like Workflow States, where the
   * editor needs every bit of height. Every other page shows its title, so
   * settings pages read the same way.
   */
  hideTitle?: boolean
  /**
   * `sm` for a short, narrow list, like Document Tags or Features. `md` for
   * forms. `lg` for tables and side-by-side editors that need the room.
   * `full` takes all the width the layout gives it, for editors that keep
   * growing sideways, like the lanes on a workflow state's events or Field
   * Rules' three columns.
   *
   * Use `full` only for explicit, exceptional cases like those. Most pages
   * should be `md` or `lg`, so lines stay readable on wide screens.
   */
  width?: 'sm' | 'md' | 'lg' | 'full'
  /**
   * Stretch the page to the bottom of the screen, and its last child with
   * it. For panes that each scroll on their own, like a list beside an
   * editor. Give that child a `minHeight` so it stays usable on short
   * screens and doesn't grow with its content.
   */
  fill?: boolean
  /** The page's `SettingsSection`s. */
  children?: ReactNode
}

/**
 * The frame for one settings page. It gives the page a set width, renders the
 * page heading (an `<h1>`), and puts even spacing between its sections.
 *
 * Put `SettingsSection`s inside it as children.
 *
 * It does not render the settings nav, scroll the page, or save anything.
 * `ProjectSettingsLayout` renders the nav and the page scrolls inside it.
 */
export default function SettingsPage({
  title,
  description,
  actions,
  back,
  hideTitle = false,
  width = 'md',
  fill = false,
  children,
}: SettingsPageProps) {
  const heading = (
    <SettingsHeading
      level="page"
      title={title}
      description={description}
      actions={actions}
    />
  )

  return (
    <Container
      // `false` turns off Container's max width, for `full`.
      maxWidth={width === 'full' ? false : width}
      disableGutters
      sx={
        fill
          ? {
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              minHeight: 0,
              pb: 2,
            }
          : { pb: 8 }
      }
    >
      {back && (
        <Button
          component={Link}
          to={back.to}
          startIcon={<ArrowBack />}
          variant="text"
          // Close to the title it belongs to, rather than a full section gap.
          // The negative margin lines the arrow up with the title's left edge.
          sx={{ alignSelf: 'flex-start', mb: 1, ml: -1 }}
        >
          {back.label}
        </Button>
      )}
      {/* Kept outside the Stack when hidden, so it doesn't add a gap. */}
      {hideTitle && <Box sx={visuallyHidden}>{heading}</Box>}
      <Stack
        spacing={5}
        sx={{
          // Content with no heading of its own, like Fields' single table,
          // sits closer to the page title than a titled section does.
          '& > [data-settings-heading] + :not([data-has-title])': { mt: 3 },
          ...(fill && {
            flexGrow: 1,
            minHeight: 0,
            '& > :last-child': { flexGrow: 1 },
          }),
        }}
      >
        {!hideTitle && heading}
        {children}
      </Stack>
    </Container>
  )
}
